"use client";

import { Box } from "@ecco/ui/box";
import H from "@ecco/ui/a11y-heading";
import { useLocale } from "next-intl";

import { useEffect, type FC } from "react";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { CommonCMS } from "@lib/constants/contentful";
import { Market } from "@config/site/types";
import type { BazaarvoiceGalleryModel } from "@lib/contentful/mappers/modules/bazaarvoiceGallery/types";

// Remove this when BazaarVoice starts supporting ko-KR and ja-JP translations (expected soon).
const EAP_MARKETS: Market[] = [
    Market.AU,
    Market.IN,
    Market.MY,
    Market.NZ,
    Market.SG,
    Market.JP,
    Market.KR,
];

// Copies the header Bazaarvoice renders in the markets it supports
const GalleryHeading: FC = () => {
    const { getMultiple } = useMicrocopy();
    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.bazaarVoice.title,
        CommonCMS.bazaarVoice.description,
    ]);
    const title = microcopies[CommonCMS.bazaarVoice.title];
    const description = microcopies[CommonCMS.bazaarVoice.description];

    return (
        <Box textAlign="center" mb={4} px={["40px", "40px", "50px", "50px"]}>
            {title && (
                <H
                    as="h2"
                    size="h2"
                    color="black"
                    fontSize={[
                        "calc(3.33333px + 3.33333vw)",
                        "calc(3.33333px + 3.33333vw)",
                        "36px",
                        "36px",
                    ]}
                    fontWeight={700}
                    mb="10px"
                >
                    {title}
                </H>
            )}
            {description && (
                <H
                    as="h3"
                    size="h3"
                    fontSize={[
                        "calc(8.12121px + 1.21212vw)",
                        "calc(8.12121px + 1.21212vw)",
                        "20px",
                        "20px",
                    ]}
                    fontWeight={500}
                    mb="20px"
                >
                    {description}
                </H>
            )}
        </Box>
    );
};

export const BazaarvoiceGalleryContainer: FC<BazaarvoiceGalleryModel> = ({ galleryId, filter }) => {
    const locale = useLocale();
    const currentMarket = locale.split("-")[1]?.toLowerCase();
    const shouldRenderTitleAndDesc = EAP_MARKETS.includes(currentMarket as Market);

    useEffect(() => {
        const crl8 = window.crl8;

        crl8?.ready?.(() => {
            if (!crl8.getAllExperiences) return;

            const isMounted = crl8
                .getAllExperiences()
                .some(
                    (experience) =>
                        experience?.isMounted && experience.options?.containerId === galleryId
                );

            // Destroy a gallery that is already on the page so it is built again.
            if (isMounted) {
                crl8.destroyExperience?.(galleryId);
            }
            crl8.createExperience?.(galleryId);
        });
    }, [galleryId]);

    return (
        <>
            {shouldRenderTitleAndDesc && <GalleryHeading />}
            <Box
                data-crl8-container-id={galleryId}
                data-crl8-locale={locale}
                data-crl8-filter={filter}
            />
        </>
    );
};
